import React, { useState, useEffect } from 'react';
import api from '../services/api';

export default function Deployments() {
  const [deployments, setDeployments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [filterCompany, setFilterCompany] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterTrigger, setFilterTrigger] = useState('');
  const [rollbackingId, setRollbackingId] = useState(null);
  const [rollbackResult, setRollbackResult] = useState({}); // deploymentId -> { status, runId, logs }
  const [rollbackLogVisible, setRollbackLogVisible] = useState({}); // deploymentId -> bool

  useEffect(() => {
    api.get('/deployments').then(r => setDeployments(r.data));
    api.get('/projects').then(r => setProjects(r.data));
    api.get('/companies').then(r => setCompanies(r.data));
    api.get('/pipelines').then(r => setPipelines(r.data));
  }, []);

  const projectName = (id) => projects.find(p => p.id === id)?.name || `#${id}`;

  const statusBadge = (s) => {
    const m = { SUCCESS: 'badge-success', FAILED: 'badge-danger', IN_PROGRESS: 'badge-warning', PENDING: 'badge-info', ROLLED_BACK: 'badge-gray' };
    return <span className={`badge ${m[s] || 'badge-gray'}`}>{s}</span>;
  };

  const filteredProjects = filterCompany
    ? projects.filter(p => p.companyId === Number(filterCompany))
    : projects;

  const filteredDeployments = deployments.filter(d => {
    if (filterProject && d.projectId !== Number(filterProject)) return false;
    if (filterCompany) {
      const proj = projects.find(p => p.id === d.projectId);
      if (!proj || proj.companyId !== Number(filterCompany)) return false;
    }
    if (filterTrigger === 'AUTO' && !d.commitId) return false;
    if (filterTrigger === 'MANUAL' && d.commitId) return false;
    return true;
  });

  const handleRollback = async (d) => {
    const pipeline = pipelines.find(p => p.projectId === d.projectId && (p.triggerType === 'GIT_PUSH_MANUAL' || p.triggerType === 'GIT_PUSH_AUTO'))
                  || pipelines.find(p => p.projectId === d.projectId);
    if (!pipeline) {
      alert('No pipeline found for this project.');
      return;
    }
    if (!window.confirm(`Rollback to version ${d.version}?\n\nThis will re-run pipeline "${pipeline.name}" with the old commit.`)) return;

    setRollbackingId(d.id);
    setRollbackLogVisible(prev => ({ ...prev, [d.id]: true }));
    setRollbackResult(prev => ({ ...prev, [d.id]: { status: 'QUEUED', runId: null, logs: 'Starting rollback...' } }));
    try {
      const { data } = await api.post(`/pipelines/${pipeline.id}/run`, {
        triggeredBy: 'rollback',
        commitSha: d.commitId || d.version,
        branch: d.branch || 'main',
      });
      setRollbackResult(prev => ({ ...prev, [d.id]: { status: 'QUEUED', runId: data.runId, logs: 'Build queued...' } }));
      // Poll logs + status every 2s
      const interval = setInterval(async () => {
        try {
          const r = await api.get(`/pipelines/runs/${data.runId}`);
          const run = r.data;
          setRollbackResult(prev => ({ ...prev, [d.id]: { status: run.status, runId: run.id, logs: run.logs || '' } }));
          if (run.status !== 'RUNNING' && run.status !== 'QUEUED') {
            clearInterval(interval);
            setRollbackingId(null);
            api.get('/deployments').then(r => setDeployments(r.data));
          }
        } catch { clearInterval(interval); setRollbackingId(null); }
      }, 2000);
    } catch (e) {
      alert('Rollback failed: ' + (e.response?.data?.error || e.message));
      setRollbackingId(null);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Deployments</h1>
        <p style={{ color: '#64748b', fontSize: 14 }}>Track deployment history across all projects</p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <select value={filterCompany} onChange={e => { setFilterCompany(e.target.value); setFilterProject(''); }}
          style={{ fontSize: 13, padding: '6px 10px', width: 'auto' }}>
          <option value="">All Companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
          style={{ fontSize: 13, padding: '6px 10px', width: 'auto' }}>
          <option value="">All Projects</option>
          {filteredProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterTrigger} onChange={e => setFilterTrigger(e.target.value)}
          style={{ fontSize: 13, padding: '6px 10px', width: 'auto' }}>
          <option value="">All Triggers</option>
          <option value="AUTO">⚡ Auto (Git Push)</option>
          <option value="MANUAL">▶ Manual</option>
        </select>
        {(filterCompany || filterProject || filterTrigger) && (
          <button onClick={() => { setFilterCompany(''); setFilterProject(''); setFilterTrigger(''); }}
            style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer' }}>Clear</button>
        )}
        <span style={{ fontSize: 13, color: '#94a3b8', marginLeft: 'auto' }}>{filteredDeployments.length} deployment(s)</span>
      </div>

      <div className="card">
        {filteredDeployments.length === 0 ? (
          <p style={{ color: '#9ca3af' }}>
            {deployments.length === 0 ? 'No deployments recorded yet. Deployments are created when pipelines run.' : 'No deployments match the selected filters.'}
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Project</th>
                <th>Environment</th>
                <th>Version</th>
                <th>Trigger</th>
                <th>Strategy</th>
                <th>Status</th>
                <th>Deployed By</th>
                <th>Time</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeployments.map(d => {
                const result = rollbackResult[d.id];
                const isRollingBack = rollbackingId === d.id;
                return (
                  <React.Fragment key={d.id}>
                  <tr>
                    <td style={{ fontWeight: 600 }}>{projectName(d.projectId)}</td>
                    <td><span className="badge badge-info">{d.environment}</span></td>
                    <td><code style={{ fontSize: 12, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{d.version}</code></td>
                    <td>
                      {d.commitId
                        ? <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>⚡ Auto</span>
                        : <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>▶ Manual</span>}
                    </td>
                    <td>{d.strategy || '-'}</td>
                    <td>{statusBadge(d.status)}</td>
                    <td>{d.deployedBy || '-'}</td>
                    <td style={{ fontSize: 13, color: '#6b7280' }}>{d.deployedAt ? new Date(d.deployedAt).toLocaleString() : '-'}</td>
                    <td>
                      {result ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600, display: 'inline-block',
                            background: result.status === 'SUCCESS' ? '#dcfce7' : result.status === 'FAILED' ? '#fee2e2' : '#fef3c7',
                            color: result.status === 'SUCCESS' ? '#166534' : result.status === 'FAILED' ? '#991b1b' : '#92400e' }}>
                            {result.status === 'SUCCESS' ? '✓ Rolled Back' : result.status === 'FAILED' ? '✗ Failed' : '⚙ Rolling back...'}
                          </span>
                          <button onClick={() => setRollbackLogVisible(prev => ({ ...prev, [d.id]: !prev[d.id] }))}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid #e2e8f0',
                              background: rollbackLogVisible[d.id] ? '#0f172a' : '#f8fafc',
                              color: rollbackLogVisible[d.id] ? '#e2e8f0' : '#374151', cursor: 'pointer', fontWeight: 600 }}>
                            {rollbackLogVisible[d.id] ? '▲ Close Log' : '📋 View Log'}
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => handleRollback(d)} disabled={!!rollbackingId}
                          style={{ fontSize: 12, padding: '4px 12px', borderRadius: 6, border: '1px solid #e2e8f0',
                            background: rollbackingId ? '#f1f5f9' : '#fff7ed', color: rollbackingId ? '#94a3b8' : '#c2410c',
                            cursor: rollbackingId ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                          ↩ Rollback
                        </button>
                      )}
                    </td>
                  </tr>
                  {result && rollbackLogVisible[d.id] && (
                    <tr>
                      <td colSpan={9} style={{ padding: 0 }}>
                        <div style={{ background: '#0f172a', padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                              background: result.status === 'SUCCESS' ? '#166534' : result.status === 'FAILED' ? '#dc2626' : '#d97706',
                              color: '#fff' }}>
                              {result.status === 'RUNNING' ? '⚙ RUNNING' : result.status === 'QUEUED' ? '⏳ QUEUED' : result.status}
                            </span>
                            <span style={{ fontSize: 11, color: '#64748b' }}>Rollback Build Log</span>
                          </div>
                          <pre style={{ color: '#e2e8f0', fontSize: 11, margin: 0, maxHeight: 250, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {result.logs || 'Waiting for logs...'}
                          </pre>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
